import { HOME, type SectionId } from '@sonder/shared'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { useAuth } from '@/auth/AuthProvider'
import { enabledSections, useAllSections, useBrandCount, useMembers, useProgress, useStaffDirectory } from '@/data/client'
import { ButtonLink, Eyebrow, ProgressNumber } from '@/ui'
import { StepSummary } from './StepSummary'
import { TopBar } from './TopBar'
import type { PageProps } from './ClientRoutes'

const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

/**
 * Where a returning client lands: how far onboarding has got and a way straight back in, every step at a glance,
 * and the rest of the portal (their Sonder team, their people, galleries, the introduction) one click away.
 */
export function ClientHome({ clientId, client, config, base }: PageProps) {
  const { user } = useAuth()
  const { docs, loading } = useAllSections(clientId)
  const brandCount = useBrandCount(clientId)
  const progress = useProgress(config, docs)
  const members = useMembers(clientId)
  const staff = useStaffDirectory()
  const sections = enabledSections(config, brandCount)

  const me = members.items.find((m) => m.id === user?.uid)
  const firstName = (me?.name || user?.displayName || '').trim().split(/\s+/)[0] || null
  const overall = progress?.overall
  const pct = overall && overall.total > 0 ? Math.round((overall.done / overall.total) * 100) : 0
  // The first step still asking something of the client; optional steps they haven't touched don't hold them up.
  const next = sections.find((s) => {
    const state = progress?.states[s.id] ?? 'todo'
    return state === 'review' || state === 'partial' || (state === 'todo' && !s.optional)
  })
  const submitted = client.status === 'submitted' && client.submittedAt
  const lead = submitted ? HOME.submitted(day(client.submittedAt!)) : (overall?.done ?? 0) > 0 ? HOME.inProgress(pct) : HOME.notStarted
  const team = [...config.team]
    .sort((a, b) => a.order - b.order)
    .map((t) => ({ ...t, staff: staff.items.find((s) => s.id === t.staffId) }))
    .filter((t) => t.staff)

  return (
    <>
      <TopBar clientName={client.name} section="Home" homeHref={base} />
      <main id="main" className="mx-auto grid w-full max-w-portal gap-12 px-6 py-section-sm md:px-10">
        <header className="grid max-w-form gap-3">
          <Eyebrow>{HOME.eyebrow}</Eyebrow>
          <h1 className="text-h2">{HOME.title(firstName)}</h1>
          <p className="text-charcoal">{lead}</p>
        </header>

        <div className="grid gap-12 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-16">
          <section aria-label="Onboarding" className="grid content-start gap-8">
            <div className="grid gap-6">
              <ProgressNumber done={overall?.done ?? 0} total={overall?.total ?? 0} label="Onboarding progress" ready={!loading && progress !== null} />
              <div>
                <ButtonLink to={next ? `${base}/onboarding/${next.id}` : `${base}/onboarding/review`}>{next ? HOME.continue(next.title) : HOME.review}</ButtonLink>
              </div>
            </div>
            <div className="grid gap-3">
              <Eyebrow tone="muted">{HOME.stepsTitle}</Eyebrow>
              <StepSummary config={config} brandCount={brandCount} progress={progress} to={(id: SectionId) => `${base}/onboarding/${id}`} label={HOME.stepsTitle} />
            </div>
          </section>

          <aside className="grid content-start gap-10">
            <Panel title={HOME.teamTitle} link={{ to: `${base}/team`, label: HOME.teamLink }}>
              {team.length > 0 && (
                <ul className="grid gap-2">
                  {team.map((t) => (
                    <li key={t.staffId} className="grid">
                      <span className="text-small text-ink">{t.staff!.name}</span>
                      <span className="text-[0.75rem] text-dim">{t.roleLabel || t.staff!.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
            <Panel title={HOME.peopleTitle} link={{ to: `${base}/people`, label: HOME.peopleLink }}>
              {!members.loading && <p className="text-small text-charcoal">{HOME.people(members.items.length)}</p>}
            </Panel>
            <Panel title={HOME.galleriesTitle} link={{ to: `${base}/galleries`, label: HOME.galleriesLink }}>
              <p className="text-small text-charcoal">{HOME.galleriesBody}</p>
            </Panel>
            <Panel title={HOME.introTitle} link={{ to: `${base}/welcome`, label: HOME.introLink }}>
              <p className="text-small text-charcoal">{HOME.introBody}</p>
            </Panel>
          </aside>
        </div>
      </main>
    </>
  )
}

function Panel({ title, link, children }: { title: string; link: { to: string; label: string }; children?: ReactNode }) {
  return (
    <section aria-label={title} className="grid content-start gap-3 border-t border-ink pt-4">
      <Eyebrow>{title}</Eyebrow>
      {children}
      <Link to={link.to} className="justify-self-start text-small underline underline-offset-4 hover:text-ink">
        {link.label}
      </Link>
    </section>
  )
}
